import { useAuth0 } from "@auth0/auth0-react"
import classNames from "classnames"
import { changeLanguage } from "i18next"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useMatch, useNavigate, useParams } from "react-router-dom"
import { ReactComponent as Tranquility } from "../../assets/logos/tranquility_black.svg"
import { ReactComponent as TranquilityFR } from "../../assets/logos/tranquility_fr_black.svg"
import { DisplayValues } from "../../constants/DisplayValues"
import { LanguageOption } from "../../types/LanguageOption"

const {
  ViewForm: { English, French },
} = DisplayValues

const getLanguageText = (
  currentLanguage: LanguageOption,
  isAdmin?: boolean,
) => {
  if (isAdmin) {
    return {
      english: English.Admin,
      french: French.Admin,
    }
  } else if (currentLanguage === "fr") {
    return {
      english: English.Inactive,
      french: French.Active,
    }
  } else {
    return {
      english: English.Active,
      french: French.Inactive,
    }
  }
}

export function Header() {
  const location = useLocation()
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const { isAuthenticated, logout } = useAuth0()

  const isAdmin = useMatch("/manage/*")

  const { province } = useParams<{ province: string }>()

  const { english, french } = useMemo(
    () =>
      getLanguageText(
        i18n.resolvedLanguage as LanguageOption,
        isAdmin ? true : false,
      ),
    [i18n.resolvedLanguage, isAdmin],
  )
  const currentProvince = province ?? "ontario"

  const onTranquilityClick = () => {
    if (location.pathname.includes("manage")) {
      navigate("/manage")
    } else {
      navigate(currentProvince)
    }
  }

  const renderLogo = () => {
    if (i18n.resolvedLanguage === "fr") {
      return (
        <TranquilityFR
          tabIndex={0}
          className="cursor-pointer"
          aria-label="Cliquez sur le logo Tranquility pour revenir à la page d'accueil"
          onClick={onTranquilityClick}
          data-testid="tranquility-logo-fr"
        />
      )
    }
    return (
      <Tranquility
        tabIndex={0}
        className="cursor-pointer"
        aria-label="Click Tranquility logo to return to home page"
        onClick={onTranquilityClick}
        data-testid="tranquility-logo"
      />
    )
  }

  const handleLogout = () => {
    logout({ returnTo: window.location.origin })
  }

  const handleAriaSelected = (id: string, value: string) => {
    document.getElementById(id)?.setAttribute("aria-selected", value)
  }

  const getFormattedVersion = () => {
    return process.env.REACT_APP_VERSION?.replace(/^v+/, "").split("-")[0] ?? ""
  }

  return (
    <div id="topNav" className="flex flex-col fixed top-0 left-0 right-0 z-50">
      <div className="flex bg-white z-50 md:px-28 border-b border-b-[color:var(--alias-color-border-weak)]">
        <div className="flex h-20 items-center justify-between w-full">
          <div className="flex">{renderLogo()}</div>
          <div className="flex">
            <div
              id="localeSwitch"
              className="flex [font-family:var(--alias-font-label-strong-font-weight)] [font-size:var(--alias-font-label-strong-font-size)] items-center"
            >
              <button
                id="english-button"
                lang="en"
                className={classNames({
                  "text-[color:var(--alias-color-text-link-default)]":
                    i18n.resolvedLanguage !== "en",
                })}
                onClick={() => changeLanguage("en")}
                onFocus={() => handleAriaSelected("english-button", "true")}
                onBlur={() => handleAriaSelected("english-button", "false")}
                aria-label={english}
              >
                {english}
              </button>
              <p className="px-1">/</p>
              <button
                id="french-button"
                lang="fr"
                className={classNames({
                  "text-[color:var(--alias-color-text-link-default)]":
                    i18n.resolvedLanguage !== "fr",
                })}
                onClick={() => changeLanguage("fr")}
                aria-label={french}
                onFocus={() => handleAriaSelected("french-button", "true")}
                onBlur={() => handleAriaSelected("french-button", "false")}
              >
                {french}
              </button>
              {isAuthenticated && (
                <div className="group">
                  <button
                    onClick={handleLogout}
                    className="ml-4 bg-white border border-[color:var(--alias-color-border-base) rounded-full cursor-pointer z-10"
                  >
                    <div className="px-5 py-2.5 text-center text-[color:var(--alias-color-text-on-outline)] [font-family:var(--alias-font-label-strong-font-weight)] [font-size:var(--alias-font-label-strong-font-size)] cursor-pointer">
                      {t("admin.topNav.logOut")}
                    </div>
                  </button>
                </div>
              )}
              <div className="group">
                <div className="px-5 py-2.5 text-center text-[color:var(--alias-color-text-on-outline)] [font-family:var(--alias-font-label-strong-font-weight)] [font-size:var(--alias-font-label-strong-font-size)] cursor-pointer">
                  {"Webapp v"}
                  {getFormattedVersion()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
